import React, { useEffect, type ReactNode } from 'react';
import ChatContext from '../context/ChatContext';
import { useDEAIChat } from '@/hooks/use-deai-chat';
import type { ChatMessage } from '../context/ChatContext';
import { useVisionCanvas } from './vision-canvas-context';
import { EngineCommand, VisionEvents } from '@akavirtual/deai-web-core';

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { broadcast, listenOn, listenOff } = useVisionCanvas();

  const {
    chatMessages,
    messageInput,
    setMessageInput,
    sendTextMessage,
    sendAudioMessage,
    addMessage,
    clearMessages,
  } = useDEAIChat({
    onSendText: (text: string) => broadcast(EngineCommand.USER_SPEAK, { type:"string", data: text }),
    onSendAudio: (blob: Blob) => broadcast(EngineCommand.USER_SPEAK, { type:"audio", data: blob }),
  });

  useEffect(() => {
    // Define typed handlers for subscriptions
    const handleNewMessage = (payload: ChatMessage) => addMessage(payload);
    const handleClear = () => clearMessages();

    listenOn(VisionEvents.AGENT_SPEAK, handleNewMessage);
    listenOn('network:cls', handleClear);
    return () => {
      listenOff(VisionEvents.AGENT_SPEAK, handleNewMessage);
      listenOff('network:cls', handleClear);
    };
  }, [listenOn, listenOff, addMessage, clearMessages]);

  const contextValue = {
    chatMessages,
    messageInput,
    setMessageInput,
    sendTextMessage,
    sendAudioMessage,
  };

  return (
    <ChatContext.Provider value={contextValue}>
      {children}
    </ChatContext.Provider>
  );
};
